import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getLeaveReport, createLeaveReportDownload } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import Echo from '../../../../Components/Sockect/Echo';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './LeaveReport.module.css';
import {
	calculateReportDateRange,
	getPeriodText,
	isReportRangeWithinMonths,
} from '../../../../Utils/ReportDateUtils';
import { clearSharedUserChannelEvent } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';

const LeaveReport = ({ filterParams }) => {
	const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
	const [leaveData, setLeaveData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [outletName, setOutletName] = useState('');
	const [periodText, setPeriodText] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [pagination, setPagination] = useState(null);
	const observer = useRef();
	const fetchLeaveReportRef = useRef();

	useEffect(() => {
		if (filterParams && filterParams.date) {
			setCurrentPage(1);
			setLeaveData([]);
			setHasMore(true);
			fetchLeaveReport(1, true);
		}
	}, [filterParams]);

	// Get outlet name
	useEffect(() => {
		if (selectedOutlet && userAssignedOutlets?.length) {
			const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
			setOutletName(outlet?.name || 'Unknown');
		}
	}, [selectedOutlet, userAssignedOutlets]);

	const fetchLeaveReport = useCallback(async (page = 1, isInitialLoad = false) => {
		try {
			if (isInitialLoad) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			// Get user_id from localStorage
			const userId = localStorage.getItem('user_id');
			if (!userId) {
				toast.error('User ID not found');
				return;
			}

			const { startDate: startDateTime, endDate: endDateTime } = calculateReportDateRange(filterParams);

		const params = {
			daterange: `${startDateTime} / ${endDateTime}`,
			outlet: filterParams.outlet || selectedOutlet,
			user_id: userId,
			page: page,
			archive: 1
		};

		if (filterParams?.staff !== undefined) {
			if (Array.isArray(filterParams.staff)) {
				params.therapist = filterParams.staff.join(',');
			} else {
				params.therapist = String(filterParams.staff || '').trim();
			}
		}
		if (filterParams.archive === 1) {
			params.archive = 1;
		}

			if (isInitialLoad) {
				setPeriodText(getPeriodText(filterParams));
			}

			const response = await getLeaveReport(params);

			if (response?.data?.success) {
				const dataList = response.data?.data?.off_days || [];
				const paginationInfo = response.data?.data?.pagination;

				if (isInitialLoad) {
					setLeaveData(dataList);
				} else {
					setLeaveData(prev => [...prev, ...dataList]);
				}

				setPagination(paginationInfo);
				
				// Check if there are more pages
				if (paginationInfo && paginationInfo.current_page >= paginationInfo.last_page) {
					setHasMore(false);
				}
			} else {
				if (isInitialLoad) {
					toast.error(response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch leave report');
					setLeaveData([]);
				}
			}
		} catch (error) {
			console.error('Error fetching leave report:', error);
			if (isInitialLoad) {
				toast.error('An error occurred while fetching leave report');
				setLeaveData([]);
			}
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	}, [filterParams, selectedOutlet]);

	// Store fetchLeaveReport in ref for use in observer
	useEffect(() => {
		fetchLeaveReportRef.current = fetchLeaveReport;
	}, [fetchLeaveReport]);

	// Intersection Observer for infinite scroll
	const lastItemRef = useCallback(node => {
		if (loading || loadingMore) return;
		if (observer.current) observer.current.disconnect();
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore && pagination) {
				const nextPage = currentPage + 1;
				if (nextPage <= pagination.last_page && fetchLeaveReportRef.current) {
					setCurrentPage(nextPage);
					fetchLeaveReportRef.current(nextPage, false);
				}
			}
		});
		if (node) observer.current.observe(node);
	}, [loading, loadingMore, hasMore, pagination, currentPage]);

	const handleDownloadCSV = async () => {
		if (!leaveData || leaveData.length === 0) {
			toast.warning('No data available to download');
			return;
		}
		if (!isReportRangeWithinMonths(filterParams, 3)) {
			toast.error('Please select a date range within 90 days to proceed.');
			return;
		}

		// Get user_id and token from localStorage
		const userId = localStorage.getItem('user_id');
		const token = localStorage.getItem('token');
		
		if (!userId) {
			toast.error('User ID not found');
			return;
		}

		if (!token) {
			toast.error('Authentication token not found');
			return;
		}

		// Create user-specific private channel name
		const channelName = `export-leaves.${userId}`;
		let channel;
		let handleEvent = null;
		let toastId = null;

		setDownloadLoading(true);
		
		// Show loading toast
		toastId = toast.loading("Downloading in progress...");
		
		try {
		// Join export channel — stopListening only (do not Echo.leave).
		channel = Echo.private(channelName);
		const eventName = 'export.status.updated';
		let handled = false;

		handleEvent = async (event) => {
			try {
				const status = event?.status || event?.data?.status;

				if (status === 'processing' || status === 'queued') {
					return;
				}

				if (handled) return;
				handled = true;

				if (status === 'failed' || status === 'error') {
					const errorMessage = event?.message || event?.data?.message || 'Export failed';
					toast.error(errorMessage);
					cleanup();
					setDownloadLoading(false);
					return;
				}

				const downloadUrl = event?.data?.download_url || 
								  event?.download_url ||
								  event?.data?.data?.download_url;

				if (!downloadUrl) {
					toast.error('Download URL not received. Please try again.');
					cleanup();
					setDownloadLoading(false);
					return;
				}

				try {
					await downloadFileWithAuth(
						downloadUrl,
						getDownloadFilename(event, downloadUrl, 'leave-report.csv')
					);
					toast.success('Downloaded successfully');
				} catch (downloadError) {
					toast.error('Failed to start download');
				}

				cleanup();
				setDownloadLoading(false);
			} catch (error) {
				handled = true;
				toast.error('Failed to process download');
				cleanup();
				setDownloadLoading(false);
			}
		};

		const cleanup = () => {
			clearSharedUserChannelEvent(channelName, eventName, channel, handleEvent);
			if (toastId) {
				toast.dismiss(toastId);
				toastId = null;
			}
		};

		clearSharedUserChannelEvent(channelName, eventName, channel);

		try {
			channel.listen(`.${eventName}`, handleEvent);
		} catch (e) {}

		try {
			const pusherChannel = Echo?.connector?.pusher?.channel?.(`private-${channelName}`);
			pusherChannel?.bind?.(eventName, handleEvent);
		} catch (e) {}

		// Error handler
		if (typeof channel.error === 'function') {
			channel.error((error) => {
				toast.error('Socket connection error');
				cleanup();
				setDownloadLoading(false);
			});
		}

		// Wait for subscription to be ready before calling API
		await new Promise((resolve) => setTimeout(resolve, 500));

			const { startDate: downloadStartDateTime, endDate: downloadEndDateTime } =
				calculateReportDateRange(filterParams);

			// Call create API to trigger report generation
			const downloadParams = {
				daterange: `${downloadStartDateTime} / ${downloadEndDateTime}`,
				outlet: filterParams.outlet || selectedOutlet,
				user_id: userId
			};

			if (filterParams?.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					downloadParams.therapist = filterParams.staff.join(',');
				} else {
					downloadParams.therapist = String(filterParams.staff || '').trim();
				}
			}
			if (filterParams.archive === 1) {
				downloadParams.archive = 1;
			}

			const createResponse = await createLeaveReportDownload(downloadParams);

			// Check for success in the response
			const isSuccess = createResponse?.data?.data?.success || createResponse?.data?.success;
			
			if (isSuccess) {
				return; // Wait for socket event
			}

			// If not successful, handle error
			if (!isSuccess) {
				const backendError = createResponse?.data?.data?.message ||
								   createResponse?.data?.message || 
								   createResponse?.message || 
								   'Failed to initiate download';
				
				// Show user-friendly error message
				let userMessage = 'Failed to generate report. ';
				
				if (backendError.includes('S3') || backendError.includes('AWS') || backendError.includes('storage')) {
					userMessage += 'Server storage configuration issue. Please contact support.';
				} else if (backendError.includes('ACL')) {
					userMessage += 'File permission error. Please contact support.';
				} else {
					userMessage = backendError.substring(0, 200);
				}
				
				toast.error(userMessage, {
					autoClose: 6000
				});
				
				cleanup();
				setDownloadLoading(false);
				return;
			}

		} catch (error) {
			const errorMessage = error?.response?.data?.message || 
							   error?.message || 
							   'An error occurred while preparing download';
			
			toast.error(errorMessage, {
				autoClose: 5000
			});
			
			if (toastId) toast.dismiss(toastId);
			setDownloadLoading(false);
			
			try {
				clearSharedUserChannelEvent(channelName, 'export.status.updated', channel, handleEvent);
			} catch (e) {}
		}
	};

	return (
		<div className={styles.leaveReportContainer}>
			{/* Header Section */}
		<ReportHeader
			outletName={outletName}
			periodText={periodText}
			onDownload={handleDownloadCSV}
			downloadLoading={downloadLoading}
			hasData={leaveData && leaveData.length > 0}
			downloadButtonText="Download CSV"
		/>

			{/* Table Section */}
			{loading ? (
				<div className={styles.loaderContainer}>
					<Loader />
				</div>
			) : leaveData && leaveData.length > 0 ? (
				<div className={styles.tableWrapper}>
					<table className={styles.leaveTable}>
						<thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
							<tr>
								<th className={styles.colWorkName}>Staff Member</th>
								<th className={styles.colDate}>Day</th>
								<th className={styles.colReason}>Reason</th>
								<th className={styles.colRemark}>Remark</th>
							</tr>
						</thead>
						<tbody>
					{leaveData.map((item, index) => {
						const isLastItem = index === leaveData.length - 1;
						return (
							<tr 
								key={item.therapist_id + '_' + index}
								ref={isLastItem ? lastItemRef : null}
							>
								<td className={styles.colWorkName}>
									{item.therapist_name || 'N/A'}
								</td>
								<td className={styles.colDate}>
									{item.activity_date_formatted || item.activity_date || 'N/A'}
								</td>
							<td className={styles.colReason}>
								{item.reason || '-'}
							</td>
							<td className={styles.colRemark}>
								{item.remark || '-'}
							</td>
						</tr>
					);
				})}
						</tbody>
					</table>
				</div>
			) : (
				<div className={styles.noData}>
					<Placeholder
						icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
						heading="No leave data found"
						para="Please try changing the filters"
					/>
				</div>
			)}
		</div>
	);
};

export default LeaveReport;

